// Copyright (c) Docugami, Inc. All rights reserved.

import { CachedDownloadStatus } from "hooks/downloader";
import { ExtractionModel } from "store/extraction/types";
import { StorageAccessor, StorageKey } from "./StorageAccessor";

export function getDebugLoadDelay(): number {
    return new StorageAccessor().getNumber(StorageKey.DebugLoadDelay, 0);
}

export function setLocalStorageOrganizationId(orgId: string) {
    new StorageAccessor().setString(StorageKey.LastOrganizationId, orgId);
}

export function getLocalStorageOrganizationId(): string | null {
    return new StorageAccessor().getString(StorageKey.LastOrganizationId);
}

export function getLocalStorageLongDownloadsInfo(): CachedDownloadStatus | null {
    return new StorageAccessor().getObject<CachedDownloadStatus>(StorageKey.ActiveLongDownloads);
}

export function setLocalStorageLongDownloadsInfo(value: CachedDownloadStatus) {
    new StorageAccessor().setObject(StorageKey.ActiveLongDownloads, value);
}

export function setSelfServiceAccessCode(code: string) {
    new StorageAccessor().setString(StorageKey.SelfServiceAccessCode, code);
}

export function getSelfServiceAccessCode(): string | null {
    return new StorageAccessor().getString(StorageKey.SelfServiceAccessCode);
}

export function setLastActiveUpdateTime(date: Date) {
    new StorageAccessor().setDate(StorageKey.LastActiveUpdateTime, date);
}

export function getLastActiveUpdateTime(): Date | null {
    return new StorageAccessor().getDate(StorageKey.LastActiveUpdateTime);
}

export function setLastActiveTime(date: Date) {
    new StorageAccessor().setDate(StorageKey.LastActiveTime, date);
}

export function getLastActiveTime(): Date | null {
    return new StorageAccessor().getDate(StorageKey.LastActiveTime);
}

export function setNumberOfChunkSuggestionsForDemoMode(numSuggestions: number) {
    new StorageAccessor().setNumber(StorageKey.NumberOfSuggestedChunksInDemoMode, numSuggestions);
}

export function getNumberOfChunkSuggestionsForDemoMode(): number {
    return new StorageAccessor().getNumber(StorageKey.NumberOfSuggestedChunksInDemoMode, 20);
}

export function setFirstArrivalComplete(orgId: string) {
    new StorageAccessor(window.localStorage, `docugami.${orgId}.`).setBoolean(StorageKey.FirstArrival, false);
}

export function getIsFirstArrival(orgId: string): boolean | undefined {
    return new StorageAccessor(window.localStorage, `docugami.${orgId}.`).getBoolean(StorageKey.FirstArrival);
}

export function getDefaultProjectId(orgId: string): string | null {
    return new StorageAccessor(window.localStorage, `docugami.${orgId}.`).getString(StorageKey.DefaultProjectId);
}

export function setDefaultProjectId(orgId: string, projectId?: string | null): void {
    const accessor = new StorageAccessor(window.localStorage, `docugami.${orgId}.`)
    if (!!projectId) {
        accessor.setString(StorageKey.DefaultProjectId, projectId);
    }
    else {
        accessor.removeKey(StorageKey.DefaultProjectId);
    }
}

export function getReportsChatWidth(): number {
    return new StorageAccessor().getNumber(StorageKey.ReportsChatWidth, 22);
}

export function setReportsChatWidth(width: number): void {
    new StorageAccessor().setNumber(StorageKey.ReportsChatWidth, width);
}

export function getReportsChatCollapsed(): boolean {
    return new StorageAccessor().getBoolean(StorageKey.ReportsChatCollapsed, false);
}

export function setReportsChatCollapsed(collapsed: boolean): void {
    new StorageAccessor().setBoolean(StorageKey.ReportsChatCollapsed, collapsed);
}

export function getExtractionModel(): ExtractionModel | null {
    const stored = new StorageAccessor().getString(StorageKey.ExtractionModel);
    // Validate that stored value is a valid enum member (handles legacy values like "MaverickFireworks")
    if (stored && Object.values(ExtractionModel).includes(stored as ExtractionModel)) {
        return stored as ExtractionModel;
    }
    return null;
}

export function setExtractionModel(model: ExtractionModel): void {
    new StorageAccessor().setString(StorageKey.ExtractionModel, model);
}
